/**
 * SMÅKEN — the culinary map of Småland.
 *
 * Everything the public site renders is derived from this file. It is written
 * as flat array literals so it can later be swapped for a CMS/database without
 * touching a single component.
 */

import { CATERING, CateringItem } from './catering';

export type Kind = 'ata' | 'fika' | 'handla' | 'dricka' | 'upplev';

export interface KindMeta {
  id: Kind;
  label: string;
  sub: string;
  image: string;
  /** Editorial accent used for this category's colour block. */
  accent: string;
  ink: string;
}

export const KINDS: KindMeta[] = [
  {
    id: 'ata',
    label: 'ÄTA',
    sub: 'Restauranger & krogar',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/00684a61-4f15-465c-9514-8a6b9f133b9b.jpg',
    accent: '#E42616',
    ink: '#F7F3EC',
  },
  {
    id: 'fika',
    label: 'FIKA',
    sub: 'Bagerier & caféer',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/264b58b6-1c66-481c-9dc4-a443e6930eeb.jpg',
    accent: '#F4C928',
    ink: '#111111',
  },
  {
    id: 'handla',
    label: 'HANDLA',
    sub: 'Producenter & gårdsbutiker',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/9f77d122-97ed-4274-88c9-691cf240cca3.jpg',
    accent: '#315C3A',
    ink: '#F7F3EC',
  },
  {
    id: 'dricka',
    label: 'DRICKA',
    sub: 'Kaffe, dryck & barer',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/73db5e3b-fdcd-4519-9a53-96913e9fba08.jpg',
    accent: '#2015B8',
    ink: '#F7F3EC',
  },
  {
    id: 'upplev',
    label: 'UPPLEV',
    sub: 'Events & upplevelser',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/6f8843ef-e486-4410-832d-1db1ed41e3f6.jpg',
    accent: '#8E2638',
    ink: '#F7F3EC',
  },
];

/** Positions on the stylised Småland map, as percentages of the viewBox. */
export const CITIES: Record<string, { x: number; y: number }> = {
  Jönköping: { x: 26, y: 16 },
  Eksjö: { x: 50, y: 24 },
  Vetlanda: { x: 52, y: 40 },
  Västervik: { x: 82, y: 26 },
  Oskarshamn: { x: 82, y: 45 },
  Växjö: { x: 41, y: 58 },
  Kalmar: { x: 79, y: 66 },
  Ljungby: { x: 22, y: 62 },
  Älmhult: { x: 30, y: 80 },
};

export interface Place {
  slug: string;
  name: string;
  city: keyof typeof CITIES | string;
  kind: Kind;
  /** Reads as the strap under the name: "Bageri · Café · Hantverk". */
  tags: string[];
  /** One confident sentence. This is the voice of the whole platform. */
  blurb: string;
  description: string[];
  /** "Det här gillar vi" — three specific, opinionated reasons. */
  weLike: string[];
  image: string;
  gallery: string[];
  address: string;
  hours: { day: string; time: string }[];
  price: 1 | 2 | 3;
  /** Editorial stamp shown on the card, e.g. "NYTT!" or "MÅSTE TESTAS". */
  stamp?: string;
  isNew?: boolean;
  /** Which action the profile page leads with. */
  action: 'boka' | 'bestall' | 'besok';
  instagram?: string;
  email?: string;
  menu?: { name: string; note: string; price: string }[];
}

export type {
  CateringCategory,
  CateringItem,
  CateringUnit,
  Occasion,
} from './catering';
export { CATERING_CATEGORIES, OCCASIONS } from './catering';

export const PLACES: Place[] = [
  {
    slug: 'lilla-verket',
    name: 'LILLA VERKET',
    city: 'Växjö',
    kind: 'fika',
    tags: ['Bageri', 'Café', 'Hantverk'],
    blurb: 'Surdeg som får ta tid och en croissant som knastrar hela vägen ner i knät.',
    description: [
      'Ett litet bageri med en enda idé: gör färre saker och gör dem ordentligt. Degen jäser över natten, ugnen går från fyra på morgonen och det som är slut är slut.',
      'Lokalen rymmer knappt tjugo personer och det är en del av charmen. Kom tidigt, ta med kontanter till dricksburken och sätt dig i fönstret.',
    ],
    weLike: [
      'Croissanten på lördagar. Kom före tio.',
      'Att de skriver dagens bröd för hand på en papperslapp.',
      'Kaffet är bryggt, inte espresso. De står för det.',
    ],
    image: 'https://cdn.magicpatterns.com/uploads/siv1okUNAkxhCZsd7Pwxbr/35677327-8D71-4B0C-8305-E02C836CAB8C_2.jpg',
    gallery: [
      'https://cdn.magicpatterns.com/patterns/generated-images/0c664b63-8d57-4f98-a722-f14bd09f5a50.jpg',
      'https://cdn.magicpatterns.com/patterns/generated-images/264b58b6-1c66-481c-9dc4-a443e6930eeb.jpg',
      'https://cdn.magicpatterns.com/patterns/generated-images/eebd77e7-cfe4-4b68-804b-72efaefe699e.jpg',
    ],
    address: 'Sandgärdsgatan 12, 352 30 Växjö',
    hours: [
      { day: 'Tis–Fre', time: '07.30–15.00' },
      { day: 'Lör', time: '08.00–14.00' },
      { day: 'Sön–Mån', time: 'Stängt' },
    ],
    price: 1,
    stamp: 'MÅSTE TESTAS',
    action: 'besok',
    instagram: '@lillaverket',
    email: 'hej@lillaverket.se',
    menu: [
      { name: 'Surdegslimpa', note: 'Bakad på svenskt lantvete, 24 timmars jäsning', price: '65 kr' },
      { name: 'Croissant', note: 'Smör från Sörgården. Endast helger', price: '38 kr' },
      { name: 'Kanelbulle', note: 'Kardemumma i degen, pärlsocker på toppen', price: '32 kr' },
      { name: 'Bryggkaffe', note: 'Böna från Kalmar Kafferosteri', price: '30 kr' },
    ],
  },
  {
    slug: 'top-notch-bakery',
    name: 'TOP NOTCH BAKERY',
    city: 'Växjö',
    kind: 'fika',
    tags: ['Bageri', 'Konditori', 'Lunch'],
    blurb: 'Bakverk som ser ut som smycken och en lunch som faktiskt mättar. Ovanlig kombination.',
    description: [
      'Här möts klassiskt konditorihantverk och en ganska rebellisk lust att testa nytt. Ena veckan en perfekt pistagesnurra, nästa vecka något du aldrig sett förut.',
      'De levererar också till kontor runt om i Växjö, vilket är den bästa anledningen vi vet till att boka ett morgonmöte.',
    ],
    weLike: [
      'Specialbullen som byts varje vecka.',
      'Att lunchen serveras på riktigt porslin.',
      'Fikakorgen till kontoret — beställ dagen innan.',
    ],
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/8303ae1b-9d0f-45da-b556-99c68efa3216.jpg',
    gallery: [
      'https://cdn.magicpatterns.com/patterns/generated-images/eebd77e7-cfe4-4b68-804b-72efaefe699e.jpg',
      'https://cdn.magicpatterns.com/patterns/generated-images/96991e69-59d8-42b1-a2ff-3d943c1f0a9d.jpg',
      'https://cdn.magicpatterns.com/patterns/generated-images/8fc743b5-2ac3-4a58-8c38-642530320605.jpg',
    ],
    address: 'Storgatan 24, 352 31 Växjö',
    hours: [
      { day: 'Mån–Fre', time: '07.00–17.00' },
      { day: 'Lör', time: '09.00–15.00' },
      { day: 'Sön', time: 'Stängt' },
    ],
    price: 2,
    action: 'bestall',
    instagram: '@topnotchbakery',
    email: 'order@topnotchbakery.se',
    menu: [
      { name: 'Veckans specialbulle', note: 'Byts varje måndag', price: '45 kr' },
      { name: 'Fikakorg till kontoret', note: 'Från 8 personer, beställ dagen innan', price: '95 kr/pers' },
      { name: 'Dagens lunch', note: 'Serveras 11–14', price: '135 kr' },
      { name: 'Tårta på beställning', note: '3 dagars framförhållning', price: 'Från 420 kr' },
    ],
  },
  {
    slug: 'askelyckan',
    name: 'ASKELYCKANS KONDITORI',
    city: 'Vetlanda',
    kind: 'fika',
    tags: ['Konditori', 'Tårtor', 'Gelato'],
    blurb: 'Prinsesstårtan här är inte nostalgi. Den är bara bättre än de flesta.',
    description: [
      'Ett konditori av den gamla skolan där receptet på Budapesttårtan inte har ändrats på trettio år, för att det aldrig behövt det.',
      'Sommartid gör de egen gelato. Vintertid är det smörgåstårtorna som går ut genom dörren i tio kilos-lyft.',
    ],
    weLike: [
      'Budapesttårtan. Punkt.',
      'Belgiska våfflor på söndagar.',
      'Att de fortfarande skriver beställningar i en bok.',
    ],
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/b2fc99a2-e202-40f5-8e13-d1a02111b749.jpg',
    gallery: [
      'https://cdn.magicpatterns.com/patterns/generated-images/016bdee8-40dd-482d-8d57-0eb5dba0f1dc.jpg',
      'https://cdn.magicpatterns.com/patterns/generated-images/8bba2904-0715-48b7-bc36-8f2a04ccf2a3.jpg',
      'https://cdn.magicpatterns.com/patterns/generated-images/e82a00d0-390c-4001-b6b6-932d937dfd27.jpg',
    ],
    address: 'Storgatan 7, 574 32 Vetlanda',
    hours: [
      { day: 'Mån–Fre', time: '08.00–18.00' },
      { day: 'Lör–Sön', time: '09.00–16.00' },
    ],
    price: 1,
    action: 'bestall',
    instagram: '@askelyckan',
    email: 'info@askelyckan.se',
    menu: [
      { name: 'Prinsesstårta', note: '6–8 personer', price: '245 kr' },
      { name: 'Budapesttårta', note: '6–8 personer', price: '270 kr' },
      { name: 'Smörgåstårta räka & lax', note: 'Beställ 2 dagar innan', price: '95 kr/bit' },
      { name: 'Hantverksgelato', note: 'Säsongens smaker', price: '35 kr/kula' },
    ],
  },
  {
    slug: 'kok-11',
    name: 'KÖK 11',
    city: 'Växjö',
    kind: 'ata',
    tags: ['Restaurang', 'Säsong', 'Öppet kök'],
    blurb: 'Menyn ändras när råvaran ändras. Ibland samma dag.',
    description: [
      'Ett öppet kök där kockarna själva bär ut tallrikarna och gärna berättar var kantarellerna kom ifrån. Menyn är kort, ofta fem rätter, och skrivs om när leveransen kommer.',
      'Boka bardisken om ni är två. Det är den bästa platsen i lokalen och ingen verkar ha förstått det än.',
    ],
    weLike: [
      'Bardisken framför passet.',
      'Att brödet bakas i huset varje morgon.',
      'Vinlistan är kort och all in på småskaligt.',
    ],
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/00684a61-4f15-465c-9514-8a6b9f133b9b.jpg',
    gallery: [
      'https://cdn.magicpatterns.com/patterns/generated-images/eb3d836c-650b-45a4-9ab8-2b946d2a1f10.jpg',
      'https://cdn.magicpatterns.com/patterns/generated-images/e2a2da3a-b1f1-4c6e-9b1c-980e29940562.jpg',
      'https://cdn.magicpatterns.com/patterns/generated-images/e3835e73-b6e3-462a-b7fd-0630f4ddbf9a.jpg',
    ],
    address: 'Kronobergsgatan 11, 352 33 Växjö',
    hours: [
      { day: 'Ons–Lör', time: '17.00–23.00' },
      { day: 'Sön–Tis', time: 'Stängt' },
    ],
    price: 2,
    stamp: 'HITÅT',
    action: 'boka',
    instagram: '@kok11vaxjo',
  },
  {
    slug: 'pm-och-vanner',
    name: 'PM & VÄNNER',
    city: 'Växjö',
    kind: 'ata',
    tags: ['Restaurang', 'Fine dining', 'Smålandskt'],
    blurb: 'Smålands mest omtalade bord. Det håller fortfarande.',
    description: [
      'Institutionen i Växjö. Här har den småländska finmatlagningen fått sitt eget språk, byggt på råvaror från gårdar man kan cykla till.',
      'Ta smakmenyn första gången. Kom tillbaka och ät à la carte i baren andra gången.',
    ],
    weLike: [
      'Smakmenyn med dryckespaket helt på svensk dryck.',
      'Osten. Alltid osten.',
      'Att servisen kan varenda producent vid namn.',
    ],
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/e2a2da3a-b1f1-4c6e-9b1c-980e29940562.jpg',
    gallery: [
      'https://cdn.magicpatterns.com/patterns/generated-images/eb3d836c-650b-45a4-9ab8-2b946d2a1f10.jpg',
      'https://cdn.magicpatterns.com/patterns/generated-images/6e45c36e-5349-42b8-a31b-caf183124798.jpg',
      'https://cdn.magicpatterns.com/patterns/generated-images/478e1add-8a86-4c2d-9fbd-69c95f638067.jpg',
    ],
    address: 'Storgatan 24, 352 31 Växjö',
    hours: [
      { day: 'Tis–Lör', time: '18.00–24.00' },
      { day: 'Sön–Mån', time: 'Stängt' },
    ],
    price: 3,
    action: 'boka',
    instagram: '@pmochvanner',
  },
  {
    slug: 'sorgardens-mejeri',
    name: 'SÖRGÅRDENS MEJERI',
    city: 'Ljungby',
    kind: 'handla',
    tags: ['Gårdsbutik', 'Ost', 'Mejeri'],
    blurb: 'Fyrtio kor, en ostmästare och ett kylrum du vill flytta in i.',
    description: [
      'Ett gårdsmejeri där mjölken går tolv meter från juvret till ostkaret. Det märks i lagringen: ostarna smakar den äng korna gick på.',
      'Gårdsbutiken är öppen fyra dagar i veckan och har alltid smakbitar framme.',
    ],
    weLike: [
      'Den 18 månader lagrade hårdosten.',
      'Att man får gå in i lagringsrummet.',
      'Kaffet är gratis och alltid för starkt.',
    ],
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/dbb447cf-643f-45b3-9878-dec78c0fbeae.jpg',
    gallery: [
      'https://cdn.magicpatterns.com/patterns/generated-images/6e45c36e-5349-42b8-a31b-caf183124798.jpg',
      'https://cdn.magicpatterns.com/patterns/generated-images/9f77d122-97ed-4274-88c9-691cf240cca3.jpg',
      'https://cdn.magicpatterns.com/patterns/generated-images/e3835e73-b6e3-462a-b7fd-0630f4ddbf9a.jpg',
    ],
    address: 'Sörgården 3, 341 94 Ljungby',
    hours: [
      { day: 'Tor–Fre', time: '10.00–18.00' },
      { day: 'Lör', time: '10.00–15.00' },
    ],
    price: 2,
    action: 'besok',
    instagram: '@sorgardensmejeri',
  },
  {
    slug: 'gronsakslandet',
    name: 'GRÖNSAKSLANDET',
    city: 'Eksjö',
    kind: 'handla',
    tags: ['Gårdsbutik', 'Grönsaker', 'Odling'],
    blurb: 'Morötter som smakar morot. Det borde inte vara anmärkningsvärt, men här är vi.',
    description: [
      'Ett litet marknadsträdgårdsbruk med fyrtio sorters grönsaker och en självbetjäningsbod som fungerar på förtroende.',
      'Prenumerera på grönsakskassen och du får en anledning att laga något du inte hade tänkt laga.',
    ],
    weLike: [
      'Självbetjäningsboden och swishlappen.',
      'Grönsakskassen varje onsdag.',
      'Att de säljer det som är fult men gott, billigare.',
    ],
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/31bd1e7b-cfc1-4450-8dbf-b3501fecbec0.jpg',
    gallery: [
      'https://cdn.magicpatterns.com/patterns/generated-images/e3835e73-b6e3-462a-b7fd-0630f4ddbf9a.jpg',
      'https://cdn.magicpatterns.com/patterns/generated-images/9f77d122-97ed-4274-88c9-691cf240cca3.jpg',
      'https://cdn.magicpatterns.com/patterns/generated-images/f0c4da86-6208-4839-8f2d-738da9bfb770.jpg',
    ],
    address: 'Hultsvägen 8, 575 96 Eksjö',
    hours: [
      { day: 'Ons–Lör', time: '09.00–18.00' },
      { day: 'Sön–Tis', time: 'Självbetjäning' },
    ],
    price: 1,
    isNew: true,
    stamp: 'NYTT!',
    action: 'besok',
  },
  {
    slug: 'kafferosteriet-kalmar',
    name: 'KALMAR KAFFEROSTERI',
    city: 'Kalmar',
    kind: 'dricka',
    tags: ['Rosteri', 'Kaffebar', 'Naturvin'],
    blurb: 'Rostar på förmiddagen, häller upp naturvin på kvällen. Samma disk.',
    description: [
      'Ett mikrorosteri med en bar framför rostmaskinen. På dagen är det bryggkaffe och kardemummabullar, efter fem är det öppna flaskor och skivor.',
      'Baristorna vet exakt var varje böna kommer ifrån och blir glada om man frågar.',
    ],
    weLike: [
      'Filterkaffet på singel origin, inte espresso.',
      'Att naturvinslistan skrivs om på tavlan varje vecka.',
      'Bönpåsen med rostdatum handskrivet.',
    ],
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/73db5e3b-fdcd-4519-9a53-96913e9fba08.jpg',
    gallery: [
      'https://cdn.magicpatterns.com/patterns/generated-images/2d39ae6b-1190-4f3e-a94d-054140638faf.jpg',
      'https://cdn.magicpatterns.com/patterns/generated-images/8b1aea01-7003-4539-af32-7d71f1ddddf8.jpg',
      'https://cdn.magicpatterns.com/patterns/generated-images/264b58b6-1c66-481c-9dc4-a443e6930eeb.jpg',
    ],
    address: 'Kaggensgatan 4, 392 32 Kalmar',
    hours: [
      { day: 'Mån–Fre', time: '08.00–22.00' },
      { day: 'Lör', time: '10.00–23.00' },
      { day: 'Sön', time: '10.00–16.00' },
    ],
    price: 2,
    isNew: true,
    stamp: 'NYTT!',
    action: 'besok',
    instagram: '@kalmarkafferosteri',
  },
  {
    slug: 'bryggeriet-i-skogen',
    name: 'BRYGGERIET I SKOGEN',
    city: 'Oskarshamn',
    kind: 'dricka',
    tags: ['Bryggeri', 'Taproom', 'Öl'],
    blurb: 'Ett bryggeri mitt i granskogen med en taproom som stänger när ölen tar slut.',
    description: [
      'Vatten från egen brunn, humle från gården och en jäskällare i en gammal ladugård. Ölen är ofiltrerad och tappas ofta samma vecka den dricks.',
      'Taproomen är öppen fredag och lördag. Det finns ingen buss hit, så gör upp om skjuts i förväg.',
    ],
    weLike: [
      'Den ofiltrerade lagern direkt från tank.',
      'Att det står en vedeldad grill utanför.',
      'Ingen musik. Bara prat.',
    ],
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/7d403927-1c90-4448-b890-d0f7d2b01b2f.jpg',
    gallery: [
      'https://cdn.magicpatterns.com/patterns/generated-images/f0c4da86-6208-4839-8f2d-738da9bfb770.jpg',
      'https://cdn.magicpatterns.com/patterns/generated-images/478e1add-8a86-4c2d-9fbd-69c95f638067.jpg',
      'https://cdn.magicpatterns.com/patterns/generated-images/3f0a31e2-d5a8-4733-be03-12c2e24e3578.jpg',
    ],
    address: 'Hägerum 12, 572 92 Oskarshamn',
    hours: [
      { day: 'Fre', time: '16.00–22.00' },
      { day: 'Lör', time: '13.00–22.00' },
    ],
    price: 2,
    action: 'besok',
  },
  {
    slug: 'sjoboden',
    name: 'SJÖBODEN',
    city: 'Västervik',
    kind: 'ata',
    tags: ['Krog', 'Skaldjur', 'Sommar'],
    blurb: 'Bord på bryggan, kokta kräftor i tidningspapper och ingen dresscode alls.',
    description: [
      'En krog i en gammal sjöbod där menyn i princip bestäms av vad båtarna kom in med. Öppet från maj till september och sedan är det tyst.',
      'Kom i solnedgången. Ta en plats längst ut på bryggan.',
    ],
    weLike: [
      'Skaldjursfatet för två, som räcker till tre.',
      'Att de serverar smör i en hel klick, inte i portionsförpackning.',
      'Bryggbordet längst ut.',
    ],
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/478e1add-8a86-4c2d-9fbd-69c95f638067.jpg',
    gallery: [
      'https://cdn.magicpatterns.com/patterns/generated-images/6e45c36e-5349-42b8-a31b-caf183124798.jpg',
      'https://cdn.magicpatterns.com/patterns/generated-images/e2a2da3a-b1f1-4c6e-9b1c-980e29940562.jpg',
      'https://cdn.magicpatterns.com/patterns/generated-images/3f0a31e2-d5a8-4733-be03-12c2e24e3578.jpg',
    ],
    address: 'Norra Kajen 2, 593 30 Västervik',
    hours: [
      { day: 'Maj–Sep, dagligen', time: '12.00–22.00' },
      { day: 'Okt–Apr', time: 'Stängt' },
    ],
    price: 2,
    action: 'boka',
  },
  {
    slug: 'glasbrukets-matsal',
    name: 'GLASBRUKETS MATSAL',
    city: 'Älmhult',
    kind: 'upplev',
    tags: ['Upplevelse', 'Långbord', 'Gästkockar'],
    blurb: 'Ett långbord i ett nedlagt glasbruk. Ny kock varje månad.',
    description: [
      'Trettiotvå stolar, ett bord, en kock som bjudits in för kvällen. Man vet inte vad man ska äta förrän man sitter ner.',
      'Biljetterna släpps den första i månaden och är oftast slut samma dag.',
    ],
    weLike: [
      'Att man sitter bredvid folk man inte känner.',
      'Elden mitt i lokalen.',
      'Att kocken serverar själv.',
    ],
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/6f8843ef-e486-4410-832d-1db1ed41e3f6.jpg',
    gallery: [
      'https://cdn.magicpatterns.com/patterns/generated-images/e2a2da3a-b1f1-4c6e-9b1c-980e29940562.jpg',
      'https://cdn.magicpatterns.com/patterns/generated-images/eb3d836c-650b-45a4-9ab8-2b946d2a1f10.jpg',
      'https://cdn.magicpatterns.com/patterns/generated-images/478e1add-8a86-4c2d-9fbd-69c95f638067.jpg',
    ],
    address: 'Bruksvägen 1, 343 71 Älmhult',
    hours: [{ day: 'En kväll i månaden', time: '18.30' }],
    price: 3,
    stamp: 'BRA GREJ',
    action: 'boka',
  },
  {
    slug: 'vandrarens-kok',
    name: 'VANDRARENS KÖK',
    city: 'Jönköping',
    kind: 'upplev',
    tags: ['Upplevelse', 'Skogsvandring', 'Matlagning'],
    blurb: 'Plocka i skogen på förmiddagen, ät det du plockat på kvällen.',
    description: [
      'En heldag som börjar med en korg och slutar med ett bord. Däremellan lär du dig vilka svampar som är värda att böja sig efter.',
      'Går året runt, men september och oktober är den självklara säsongen.',
    ],
    weLike: [
      'Att kursledaren är biolog, inte kock.',
      'Elden man lagar på.',
      'Man får med sig ett recepthäfte hem.',
    ],
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/f0c4da86-6208-4839-8f2d-738da9bfb770.jpg',
    gallery: [
      'https://cdn.magicpatterns.com/patterns/generated-images/e3835e73-b6e3-462a-b7fd-0630f4ddbf9a.jpg',
      'https://cdn.magicpatterns.com/patterns/generated-images/6f8843ef-e486-4410-832d-1db1ed41e3f6.jpg',
      'https://cdn.magicpatterns.com/patterns/generated-images/3f0a31e2-d5a8-4733-be03-12c2e24e3578.jpg',
    ],
    address: 'Bondbergets naturreservat, Jönköping',
    hours: [{ day: 'Lör', time: '09.00–17.00' }],
    price: 2,
    isNew: true,
    action: 'boka',
  },
];

/* --------------------------------- Producers -------------------------------- */

export interface Producer {
  slug: string;
  name: string;
  person: string;
  city: string;
  craft: string;
  quote: string;
  image: string;
}

export const PRODUCERS: Producer[] = [
  {
    slug: 'sorgardens-mejeri',
    name: 'Sörgårdens Mejeri',
    person: 'Ingrid Falk',
    city: 'Ljungby',
    craft: 'Ostmästare',
    quote: 'En ost blir aldrig bättre än ängen korna gick på. Allt annat är teknik.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/dbb447cf-643f-45b3-9878-dec78c0fbeae.jpg',
  },
  {
    slug: 'bryggeriet-i-skogen',
    name: 'Bryggeriet i Skogen',
    person: 'Anton Hägg',
    city: 'Oskarshamn',
    craft: 'Bryggare',
    quote: 'Vi filtrerar inte. Det är inte lathet, det är en åsikt.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/7d403927-1c90-4448-b890-d0f7d2b01b2f.jpg',
  },
  {
    slug: 'gronsakslandet',
    name: 'Grönsakslandet',
    person: 'Karl-Erik Nyberg',
    city: 'Eksjö',
    craft: 'Odlare',
    quote: 'Jag har odlat samma jord i fyrtiotvå år. Den börjar bli ganska bra nu.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/31bd1e7b-cfc1-4450-8dbf-b3501fecbec0.jpg',
  },
];

/* ---------------------------------- Events ---------------------------------- */

export interface SmakenEvent {
  id: string;
  day: string;
  month: string;
  name: string;
  place: string;
  city: string;
  time: string;
  kicker: string;
  soldOut?: boolean;
}

export const EVENTS: SmakenEvent[] = [
  {
    id: 'ev-1',
    day: '14',
    month: 'SEP',
    name: 'Gästkock: Nora Lindqvist',
    place: 'Glasbrukets Matsal',
    city: 'Älmhult',
    time: '18.30',
    kicker: 'Sex rätter, ett långbord, trettiotvå stolar.',
  },
  {
    id: 'ev-2',
    day: '21',
    month: 'SEP',
    name: 'Svampvandring & eldkök',
    place: 'Vandrarens Kök',
    city: 'Jönköping',
    time: '09.00',
    kicker: 'Plocka på förmiddagen. Ät på kvällen.',
  },
  {
    id: 'ev-3',
    day: '28',
    month: 'SEP',
    name: 'Ostprovning i lagringsrummet',
    place: 'Sörgårdens Mejeri',
    city: 'Ljungby',
    time: '17.00',
    kicker: 'Åtta ostar, ett kylrum, inga sked-svar.',
    soldOut: true,
  },
  {
    id: 'ev-4',
    day: '05',
    month: 'OKT',
    name: 'Surdegskurs för nybörjare',
    place: 'Lilla Verket',
    city: 'Växjö',
    time: '08.00',
    kicker: 'Du går hem med en egen surdegsgrund.',
  },
];

/* --------------------------------- Magazine --------------------------------- */

export interface Article {
  slug: string;
  kicker: string;
  title: string;
  excerpt: string;
  image: string;
  readTime: string;
}

export const ARTICLES: Article[] = [
  {
    slug: 'fika-vaxjo',
    kicker: 'Guide',
    title: '5 ställen för riktigt bra fika i Växjö',
    excerpt: 'Vi har ätit oss igenom stan i tre veckor. Det här är listan som blev kvar.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/264b58b6-1c66-481c-9dc4-a443e6930eeb.jpg',
    readTime: '6 min',
  },
  {
    slug: 'nasta-generations-bagare',
    kicker: 'Möte',
    title: 'Möt bagaren bakom nästa generations Smålandsbageri',
    excerpt: 'Hon slutade som revisor och köpte en ugn. Fyra år senare är kön ute på gatan.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/11881659-4284-4752-8ce5-56439e00c8d1.jpg',
    readTime: '9 min',
  },
  {
    slug: 'sju-saker',
    kicker: 'Just nu',
    title: '7 saker vi äter just nu',
    excerpt: 'Kantareller, ny ost, en oväntad bulle och något som egentligen inte borde funka.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/f0c4da86-6208-4839-8f2d-738da9bfb770.jpg',
    readTime: '4 min',
  },
  {
    slug: 'en-hel-dag',
    kicker: 'Resa',
    title: 'En hel dag genom Smålands mathantverk',
    excerpt: 'Frukost i Vetlanda, lunch i Eksjö, middag i Kalmar. 214 kilometer värt varje mil.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/9f77d122-97ed-4274-88c9-691cf240cca3.jpg',
    readTime: '12 min',
  },
];

/* ---------------------------------- Helpers --------------------------------- */

export const SMAKEN_LOGO =
  'https://cdn.magicpatterns.com/uploads/xqH8AbYdWpUuk9dTngEuGD/Black_and_White_Minimalist_Typographical_Gallery_Logo.png';

export const HERO_IMAGE =
  'https://cdn.magicpatterns.com/patterns/generated-images/4d216c6c-f45d-47c3-a6f9-792c17d6d6aa.jpg';

export const JOIN_IMAGE =
  'https://cdn.magicpatterns.com/patterns/generated-images/9647f4d7-1cec-4bbf-95a3-2f0433ad8bb7.jpg';

export const TICKER = [
  'SURDEG',
  'KANTARELLER',
  'GÅRDSOST',
  'NATURVIN',
  'KARDEMUMMABULLAR',
  'LINGON',
  'MIKROROSTERI',
  'LÅNGBORD',
  'GLASS PÅ GÅRDEN',
  'NYBAKAT',
];

/* --------------------------------- Catering --------------------------------- */

export interface CateringOffer extends CateringItem {
  placeSlug: string;
  placeName: string;
  placeCity: string;
}

/** Every catering item on the platform, flattened with its partner. */
export const ALL_CATERING: CateringOffer[] = PLACES.flatMap((place) =>
  (CATERING[place.slug] ?? []).map((item) => ({
    ...item,
    placeSlug: place.slug,
    placeName: place.name,
    placeCity: String(place.city),
  }))
);

export const cateringFor = (slug: string): CateringOffer[] =>
  ALL_CATERING.filter((item) => item.placeSlug === slug);

export const formatPrice = (value: number): string =>
  `${new Intl.NumberFormat('sv-SE').format(value)} kr`;

export const kindLabel = (kind: Kind): string =>
  KINDS.find((k) => k.id === kind)?.label ?? '';

export const placesByKind = (kind: Kind): Place[] => PLACES.filter((p) => p.kind === kind);
